import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from '../layout/Container';
import Button from '../components/Button';
import VideoPlayer from '../components/VideoPlayer';
import { Bracket as TournamentBracket } from './TournamentDetails';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const EventDetails = () => {
  const { slug } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useSelector(state => state.user.data);
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  // Paid events use "Get Access" (purchase+register) directly; no purchase modal needed here.
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [povStreams, setPovStreams] = useState([]);
  const [selectedPov, setSelectedPov] = useState(null);

  // Fetch event details and user info
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        // Fetch event details
        const eventResponse = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}`, {
          withCredentials: true
        });
        console.log('🎉 Event details loaded:', eventResponse.data);
        setEvent(eventResponse.data.event);

        const isTournamentEvent = eventResponse.data.event?.category?.slug === 'tournament';

        // Tournament: load accepted players/streamers list (public endpoint)
        if (isTournamentEvent) {
          try {
            const playersRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}/players`, {
              withCredentials: true
            });
            setPlayers(playersRes.data?.players || []);
          } catch (e) {
            setPlayers([]);
          }
        } else {
          setPlayers([]);
        }

        // Multi-POV: load live streams linked to this event (tournament POVs)
        try {
          const povRes = await axios.get(`/api/stream/streams/by-event/${eventResponse.data.event.id}`, {
            withCredentials: true
          });
          const streams = povRes.data?.streams || [];
          setPovStreams(streams);
          setSelectedPov((prev) => {
            if (!streams.length) return null;
            if (prev && streams.some(s => s.username === prev.username)) {
              return streams.find(s => s.username === prev.username) || streams[0];
            }
            return streams[0];
          });
        } catch (e) {
          setPovStreams([]);
          setSelectedPov(null);
        }

        // Fetch participants only when logged-in (needed for access/registration state).
        // Guests can still view the page and see spectators count via event.spectatorsCount.
        let loadedParticipants = [];
        if (user?.id) {
          try {
            const participantsResponse = await axios.get(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${eventResponse.data.event.id}`, {
              withCredentials: true
            });
            console.log('👥 Participants loaded:', participantsResponse.data);
            loadedParticipants = participantsResponse.data.participants || [];
            setParticipants(loadedParticipants);
          } catch (e) {
            setParticipants([]);
          }
        } else {
          setParticipants([]);
        }

        const isPaid = parseFloat(eventResponse.data.event.ticketPrice || 0) > 0;

        // Check if user is registered / has ticket
        if (user && loadedParticipants.length > 0) {
          const userParticipant = loadedParticipants.find(
            p => p.userId === user.id
          );
          setIsRegistered(!!userParticipant);

          // Paid events: ticket is represented by ticketId presence (not status).
          // Free events: treat approved participant as having access.
          if (isPaid) {
            setHasTicket(!!userParticipant?.ticketId);
          } else {
            const approvedParticipant = loadedParticipants.find(
              p => p.userId === user.id && p.status === 'approved'
            );
            setHasTicket(!!approvedParticipant);
          }
        } else {
          setIsRegistered(false);
          setHasTicket(false);
        }

      } catch (error) {
        console.error('❌ Error fetching event details:', error);
        toast.error(t('eventdetails.failed_to_load', 'Failed to load event details'));
        history.push('/events');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [slug, history, user, t]);

  const openLoginModal = () => {
    window.dispatchEvent(new CustomEvent("corearena:auth:open", { detail: { tabIndex: 1 } }));
  };

  const handleRegistration = async () => {
    if (!user?.id) {
      toast.info(t('eventdetails.please_login_to', 'Please login to register for events'));
      openLoginModal();
      return;
    }

    // Prevent joining past events (endDate in the past)
    if (event?.endDate && new Date(event.endDate).getTime() < Date.now()) {
      toast.error(t('eventdetails.event_ended', 'Event Ended'));
      return;
    }

    try {
      setRegistering(true);
      const isPaid = parseFloat(event.ticketPrice || 0) > 0;

      if (isRegistered) {
        if (isPaid) {
          toast.info(t('eventdetails.paid_event_unregister_disabled', 'This is a paid event. Access is managed by your ticket; unregistration is disabled.'));
          return;
        }
        // Unregister
        await axios.delete(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {
          withCredentials: true
        });
        setIsRegistered(false);
        toast.success(t('eventdetails.successfully_unregistered_from', 'Successfully unregistered from event'));
      } else {
        // Register
        const regRes = await axios.post(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {}, {
          withCredentials: true
        });
        setIsRegistered(true);
        if (isPaid) {
          const ticketId = regRes.data?.ticketId;
          if (ticketId) setHasTicket(true);
          toast.success(t('eventdetails.access_granted', 'Access granted!'));
        } else {
          toast.success(t('eventdetails.registered_success', 'Successfully registered for event!'));
        }
      }

      // Refresh participants
      const participantsResponse = await axios.get(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {
        withCredentials: true
      });
      const refreshedParticipants = participantsResponse.data.participants || [];
      setParticipants(refreshedParticipants);

      if (user && refreshedParticipants.length > 0) {
        const userParticipant = refreshedParticipants.find(p => p.userId === user.id);
        setIsRegistered(!!userParticipant);
        if (isPaid) {
          setHasTicket(!!userParticipant?.ticketId);
        } else {
          const approvedParticipant = refreshedParticipants.find(
            p => p.userId === user.id && p.status === 'approved'
          );
          setHasTicket(!!approvedParticipant);
        }
      } else {
        setIsRegistered(false);
        setHasTicket(false);
      }

    } catch (error) {
      console.error('❌ Registration error:', error);
      const status = error?.response?.status;
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.msg ||
        error?.message ||
        'Registration failed';

      const norm = String(msg).toLowerCase();
      const isPaid = parseFloat(event?.ticketPrice || 0) > 0;
      const looksLikeInsufficient =
        status === 402 ||
        status === 403 ||
        norm.includes('solde insuffisant') ||
        (norm.includes('insuff') && norm.includes('solde')) ||
        norm.includes('not enough');

      if (isPaid && looksLikeInsufficient) {
        const returnTo = `${location.pathname}${location.search}`;
        try {
          localStorage.setItem('postGcoinPurchaseRedirect', returnTo);
        } catch (_) {
          // ignore
        }
        toast.error(t('eventWatch.insufficientBalanceRedirect', 'Insufficient balance. Redirecting to buy G-Coins...'));
        const params = new URLSearchParams({ redirect: returnTo, reason: 'insufficient_balance' });
        history.push(`/gcoin-store?${params.toString()}`);
        return;
      }

      toast.error(msg);
    } finally {
      setRegistering(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!user?.id) {
      toast.info(t('eventdetails.please_login_redeem', 'Please login to redeem a ticket code'));
      openLoginModal();
      return;
    }

    if (!redeemCode.trim()) {
      setRedeemError(t('eventdetails.enter_valid_code', 'Please enter a valid code'));
      return;
    }

    try {
      setRedeeming(true);
      setRedeemError('');

      // Redeem ticket code via ticket service
      await axios.post(ENV_CONFIG.TICKET_REDEEM_ENDPOINT, {
        userId: user.id.toString(),
        eventId: event.id,
        code: redeemCode.trim()
      }, {
        withCredentials: true
      });

      // After redeeming, register as participant (if not already)
      try {
        await axios.post(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {}, {
          withCredentials: true
        });
      } catch (e) {
        // Ignore "already registered" errors
      }

      // Refresh participants list
      try {
        const participantsResponse = await axios.get(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${event.id}`, {
          withCredentials: true
        });
        const refreshedParticipants = participantsResponse.data.participants || [];
        setParticipants(refreshedParticipants);

        const userParticipant = refreshedParticipants.find(p => p.userId === user.id);
        setIsRegistered(!!userParticipant);

        const isPaid = parseFloat(event.ticketPrice || 0) > 0;
        if (isPaid) setHasTicket(!!userParticipant?.ticketId);
        else {
          const approvedParticipant = refreshedParticipants.find(
            p => p.userId === user.id && p.status === 'approved'
          );
          setHasTicket(!!approvedParticipant);
        }
      } catch (e) {
        // Silent refresh failure
      }

      toast.success(t('eventdetails.ticket_code_redeemed', 'Ticket code redeemed successfully!'));
      setShowRedeemModal(false);
      setRedeemCode('');
    } catch (error) {
      console.error('❌ Redeem code error:', error);
      const msg = error.response?.data?.message || t('eventdetails.failed_redeem_code', 'Failed to redeem code');
      setRedeemError(msg);
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return '#10B981';
      case 'active': return '#F59E0B';
      case 'completed': return '#6B7280';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return t('eventdetails.upcoming_event', 'Upcoming Event');
      case 'active': return t('eventdetails.event_live', 'Event Live');
      case 'completed': return t('eventdetails.event_ended', 'Event Ended');
      case 'cancelled': return t('eventdetails.event_cancelled', 'Event Cancelled');
      default: return status;
    }
  };

  const isTournament = event?.category?.slug === 'tournament';
  const isStreamEvent = event?.category?.slug === 'stream';
  const isWorkshopEvent = event?.category?.slug === 'workshop';
  const isStreamLiveForEvent = isStreamEvent && (povStreams || []).length > 0;
  const isWorkshopLiveForEvent = isWorkshopEvent && (povStreams || []).length > 0;

  // Tournament roles are stored in eventStreamer (players endpoint), not in participants.
  // An active tournament player/streamer must not be prompted to "Get Access" for the event.
  const isOrganizer = !!user?.id && String(user.id) === String(event?.createdBy);
  const isTournamentAssignee =
    isTournament &&
    !!user?.id &&
    (players || []).some((p) => String(p?.user?.id) === String(user.id));
  const hasPaidAccess = !!hasTicket || isOrganizer || isTournamentAssignee;

  // Spectators count (exclude players/streamers). Prefer backend computed count.
  const spectatorsCount = (() => {
    if (!event) return 0;
    if (typeof event.spectatorsCount === 'number') return event.spectatorsCount;
    if (!isTournament) return participants.length;
    const playerIds = new Set((players || []).map(p => String(p.user?.id)));
    return (participants || []).filter(p => !playerIds.has(String(p.userId))).length;
  })();

  const canRegister = () => {
    if (!event) return false;
    if (event.status !== 'published') return false;
    if (event.endDate && new Date(event.endDate).getTime() < Date.now()) return false;
    if (event.maxParticipants && participants.length >= event.maxParticipants) return false;
    return true;
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <div>{t('eventdetails.loading_event_details', 'Loading event details...')}</div>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <div>{t('eventdetails.event_not_found', 'Event not found')}</div>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      {/* Back Button */}
      <BackButton>
        <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
          {t('eventdetails.back_to_events', '← Back to Events')}
        </button>
      </BackButton>

      {/* Event Header */}
      <EventHeader>
        {event.bannerImage && (
          <EventCoverImage
            src={event.bannerImage}
            alt={event.name}
          />
        )}
        <EventHeaderOverlay>
          <EventBadge color={event.category?.color || '#6B7280'}>
            {event.category?.icon} {event.category?.name}
          </EventBadge>
          <EventStatus color={getStatusColor(event.status)}>
            {getStatusText(event.status)}
          </EventStatus>
        </EventHeaderOverlay>
      </EventHeader>

      {/* Event Content */}
      <EventContent>
        <EventMainInfo>
          <EventTitle>{event.name}</EventTitle>
          <EventDescription>{event.description}</EventDescription>

          {/* Category-specific helpers */}
          {event.category?.slug === 'stream' && (
            <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>
              {t('eventdetails.stream_helper', "This is a live stream event. When the event is live, you can watch it on the organizer's stream page.")}
            </p>
          )}
          {event.category?.slug === 'workshop' && (
            <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>
              {t('eventdetails.workshop_helper', 'This is an interactive workshop. Seats may be limited; register early to secure your spot.')}
            </p>
          )}

          <EventMetaGrid>
            <EventMetaItem>
              <MetaLabel>📅 {t('event.startDate', 'Start date')}</MetaLabel>
              <MetaValue>{formatDate(event.startDate)}</MetaValue>
            </EventMetaItem>

            {event.endDate && (
              <EventMetaItem>
                <MetaLabel>🏁 {t('event.endDate', 'End date')}</MetaLabel>
                <MetaValue>{formatDate(event.endDate)}</MetaValue>
              </EventMetaItem>
            )}

            <EventMetaItem>
              <MetaLabel>👥 {t('eventdetails.spectators', 'Spectators')}</MetaLabel>
              <MetaValue>
                {isTournament ? spectatorsCount : participants.length}
                {event.maxParticipants && ` / ${event.maxParticipants}`}
              </MetaValue>
            </EventMetaItem>

            {event.location && (
              <EventMetaItem>
                <MetaLabel>📍 {t('eventdetails.location', 'Location')}</MetaLabel>
                <MetaValue>{event.location}</MetaValue>
              </EventMetaItem>
            )}

            {event.ticketPrice > 0 && (
              <EventMetaItem>
                <MetaLabel>💰 {t('eventdetails.price', 'Price')}</MetaLabel>
                <MetaValue>{event.ticketPrice} {t('common.gcoins', 'G-Coins')}</MetaValue>
              </EventMetaItem>
            )}

            <EventMetaItem>
              <MetaLabel>👤 {t('eventdetails.organizer', 'Organizer')}</MetaLabel>
              <MetaValue>{event.organizer?.username || event.organizer?.name || event.createdBy}</MetaValue>
            </EventMetaItem>
          </EventMetaGrid>

          {/* Registration / Access Buttons */}
          <RegistrationSection>
            {user?.id && String(user.id) === String(event.createdBy) && (
              <>
                {/* Organizer manage entry point */}
                {event.category?.slug === 'tournament' ? (
                  <Link to={`/events/${event.slug}/dashboard`}>
                    <Button variant="secondary" size="large">
                      {t('eventdetails.manage_event', 'Manage Event')}
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/stream-dashboard?eventSlug=${event.slug}`}>
                    <Button variant="secondary" size="large">
                      {t('eventdetails.manage_stream', 'Manage Stream')}
                    </Button>
                  </Link>
                )}
              </>
            )}

            {user?.id && String(user.id) !== String(event.createdBy) && (
              <>
                {/* Stream viewer primary CTA when live */}
                {isStreamEvent && (
                  <>
                    {event.ticketPrice > 0 ? (
                      hasTicket ? (
                        <Link to={`/events/${event.slug}/watch`}>
                          <Button
                            variant="primary"
                            size="large"
                            disabled={!isStreamLiveForEvent}
                          >
                            {isStreamLiveForEvent ? t('eventdetails.open_stream', 'Open Stream') : t('eventdetails.stream_not_live_yet', 'Stream Not Live Yet')}
                          </Button>
                        </Link>
                      ) : null
                    ) : (
                      <Link to={`/events/${event.slug}/watch`}>
                        <Button
                          variant="primary"
                          size="large"
                          disabled={!isStreamLiveForEvent}
                        >
                          {isStreamLiveForEvent ? t('eventdetails.open_stream', 'Open Stream') : t('eventdetails.stream_not_live_yet', 'Stream Not Live Yet')}
                        </Button>
                      </Link>
                    )}
                  </>
                )}

                {/* Workshop/Tournament CTA */}
                {(isWorkshopEvent || isTournament) && (
                  event.ticketPrice > 0 ? (
                    !hasPaidAccess ? (
                      <>
                        <Button
                          variant="primary"
                          size="large"
                          onClick={handleRegistration}
                          disabled={registering || !canRegister()}
                        >
                          {registering
                            ? t('common.processing', 'Processing...')
                            : `${t('eventdetails.get_access', 'Get Access')} – ${event.ticketPrice} ${t('common.gcoins', 'G-Coins')}`}
                        </Button>
                        <Button
                          variant="outline"
                          size="large"
                          style={{ marginLeft: '0.75rem' }}
                          onClick={() => setShowRedeemModal(true)}
                        >
                          {t('eventdetails.have_ticket_code', 'I have a ticket code')}
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="large" disabled>
                        {t('eventdetails.access_granted', 'Access granted')}
                      </Button>
                    )
                  ) : (
                    <Button
                      variant={isRegistered ? "danger" : "primary"}
                      size="large"
                      onClick={handleRegistration}
                      disabled={registering || (!isRegistered && !canRegister())}
                    >
                      {registering
                        ? t('common.processing', 'Processing...')
                        : isRegistered
                          ? t('eventdetails.unregister', 'Unregister')
                          : canRegister()
                            ? t('eventdetails.register_now', 'Register Now')
                            : t('eventdetails.registration_closed', 'Registration Closed')}
                    </Button>
                  )
                )}

                {/* Paid access CTA for Stream */}
                {isStreamEvent &&
                  event.ticketPrice > 0 && !hasTicket && (
                    <>
                      <Button
                        variant="primary"
                        size="large"
                        onClick={handleRegistration}
                        disabled={registering || (event?.endDate && new Date(event.endDate).getTime() < Date.now())}
                      >
                        {registering
                          ? t('common.processing', 'Processing...')
                          : `${t('eventdetails.get_access', 'Get Access')} – ${event.ticketPrice} ${t('common.gcoins', 'G-Coins')}`}
                      </Button>
                      <Button
                        variant="outline"
                        size="large"
                        style={{ marginLeft: '0.75rem' }}
                        onClick={() => setShowRedeemModal(true)}
                      >
                        {t('eventdetails.have_ticket_code', 'I have a ticket code')}
                      </Button>
                    </>
                  )}
              </>
            )}

            {!user?.id && (
              <Button variant="primary" size="large" onClick={openLoginModal}>
                {t('eventdetails.login_to_register', 'Login to Register')}
              </Button>
            )}
          </RegistrationSection>

          {/* Tournament Integration - Embedded Bracket (no iframe) */}
          {event.category?.slug === 'tournament' && event.tournamentId && (
            <TournamentSection>
              <h3>{t('eventdetails.tournament_bracket', 'Tournament & Bracket')}</h3>
              <p style={{ marginBottom: '1rem', color: '#718096' }}>
                {t('eventdetails.bracket_helper', 'Live bracket generated from tournament matches. Updates automatically as results are reported.')}
              </p>

              <div
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #2D3748',
                  padding: '16px',
                  backgroundColor: '#0f172a',
                  marginBottom: '16px',
                }}
              >
                <TournamentBracket tournamentId={event.tournamentId} />
              </div>

              {/* Multi-POV (multiple streamers can be live for the same event) */}
              {(povStreams.length > 0 && (event.ticketPrice <= 0 || hasPaidAccess)) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0' }}>{t('eventdetails.live_povs', 'Live POVs')}</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {povStreams.map((s) => (
                      (() => {
                        const isActive = selectedPov?.username === s.username;
                        return (
                      <button
                        key={s.username}
                        type="button"
                        onClick={() => setSelectedPov(s)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: isActive
                            ? `1px solid ${theme.color.primary}`
                            : `1px solid ${theme.mode === 'dark' ? '#2D3748' : theme.color.border}`,
                          background: isActive
                            ? (theme.mode === 'dark' ? 'rgba(102,126,234,0.15)' : 'rgba(69,105,255,0.12)')
                            : (theme.mode === 'dark' ? '#0b1220' : theme.color.secondaryBg),
                          color: theme.mode === 'dark' ? '#E5E7EB' : theme.color.darkerText,
                          cursor: 'pointer'
                        }}
                      >
                        <img
                          src={s.avatar || 'https://via.placeholder.com/24'}
                          alt={s.username}
                          style={{ width: 24, height: 24, borderRadius: 999, objectFit: 'cover' }}
                        />
                        <span style={{ fontWeight: 600 }}>{s.username}</span>
                      </button>
                        );
                      })()
                    ))}
                  </div>

                  {selectedPov?.playbackLink ? (
                    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #2D3748', background: '#000' }}>
                      <VideoPlayer
                        key={selectedPov.playbackLink}
                        type="hls"
                        src={selectedPov.playbackLink}
                        isLive={true}
                        isMute={false}
                        autoPlay={true}
                        id="event-multi-pov-player"
                      />
                    </div>
                  ) : (
                    <div style={{ color: '#9CA3AF', fontSize: 14 }}>{t('eventWatch.noPlaybackYet', 'No live playback available yet.')}</div>
                  )}

                  {selectedPov?.username && (
                    <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                      <Link to={`/events/${event.slug}/watch?pov=${encodeURIComponent(selectedPov.username)}`}>
                        <Button variant="primary" size="small">{t('eventdetails.watch_seamless_pov', 'Watch (Seamless POV)')}</Button>
                      </Link>
                      {/* Do not link directly to /stream/:username (it can bypass paid access UX). */}
                    </div>
                  )}
                </div>
              )}
            </TournamentSection>
          )}

          {/* Ticket Purchase Section (shown in modal) */}
        </EventMainInfo>

        {/* Sidebar */}
        <EventSidebar>
          {(isStreamEvent || isWorkshopEvent) ? (
            <SidebarSection>
              <SidebarTitle>{isStreamEvent ? t('eventdetails.live_stream', 'Live Stream') : t('eventdetails.live_workshop', 'Live Workshop')}</SidebarTitle>
              {(isStreamEvent ? isStreamLiveForEvent : isWorkshopLiveForEvent) ? (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ color: '#9CA3AF' }}>{isStreamEvent ? t('eventdetails.stream_is_live', 'Stream is live.') : t('eventdetails.workshop_is_live', 'Workshop is live.')}</span>
                  <Link to={`/events/${event.slug}/watch`}>
                    <Button variant="primary" size="small">{isStreamEvent ? t('eventdetails.open_stream', 'Open Stream') : t('eventdetails.open_workshop', 'Open Workshop')}</Button>
                  </Link>
                </div>
              ) : (
                <div style={{ color: '#9CA3AF' }}>
                  {isStreamEvent ? t('eventdetails.stream_not_live', 'Stream is not live yet.') : t('eventdetails.workshop_not_live', 'Workshop is not live yet.')}
                </div>
              )}
            </SidebarSection>
          ) : isTournament ? (
            <SidebarSection>
              <SidebarTitle>
                {t('eventdetails.players_streamers_accepted', 'Players / Streamers (Accepted)')} ({players.length})
              </SidebarTitle>
              <ParticipantsList>
                {players.length === 0 ? (
                  <EmptyParticipants>{t('eventdetails.no_accepted_players', 'No accepted players yet')}</EmptyParticipants>
                ) : (
                  players.slice(0, 10).map((p) => (
                    <ParticipantItem key={p.id}>
                      <ParticipantAvatar>
                        {p.user?.avatar ? (
                          <img src={p.user.avatar} alt={p.user.username} />
                        ) : (
                          <div>{p.user?.username?.charAt(0) || '?'}</div>
                        )}
                      </ParticipantAvatar>
                      <ParticipantInfo>
                        <ParticipantName>{p.user?.username}</ParticipantName>
                        <ParticipantStatus status={p.status}>
                          {p.status}
                        </ParticipantStatus>
                      </ParticipantInfo>
                    </ParticipantItem>
                  ))
                )}
                {players.length > 10 && (
                  <ShowMoreParticipants>
                    +{players.length - 10} {t('eventdetails.more_players', 'more players')}
                  </ShowMoreParticipants>
                )}
              </ParticipantsList>
            </SidebarSection>
          ) : (
            <SidebarSection>
              <SidebarTitle>{t('eventdetails.spectators', 'Spectators')} ({participants.length})</SidebarTitle>
              <ParticipantsList>
                {participants.length === 0 ? (
                  <EmptyParticipants>{t('eventdetails.no_spectators_yet', 'No spectators yet')}</EmptyParticipants>
                ) : (
                  participants.slice(0, 10).map((participant) => (
                    <ParticipantItem key={participant.id}>
                      <ParticipantAvatar>
                        {participant.user?.profilePicture ? (
                          <img src={participant.user.profilePicture} alt={participant.user.name} />
                        ) : (
                          <div>{participant.user?.name?.charAt(0) || '?'}</div>
                        )}
                      </ParticipantAvatar>
                      <ParticipantInfo>
                        <ParticipantName>{participant.user?.name || participant.user?.username}</ParticipantName>
                        <ParticipantStatus status={participant.status}>
                          {participant.status}
                        </ParticipantStatus>
                      </ParticipantInfo>
                    </ParticipantItem>
                  ))
                )}
                {participants.length > 10 && (
                  <ShowMoreParticipants>
                    +{participants.length - 10} {t('eventdetails.more_participants', 'more participants')}
                  </ShowMoreParticipants>
                )}
              </ParticipantsList>
            </SidebarSection>
          )}

          {/* Event Rules/Info */}
          {event.rules && (
            <SidebarSection>
              <SidebarTitle>{t('eventdetails.rules_information', 'Rules & Information')}</SidebarTitle>
              <EventRules>{event.rules}</EventRules>
            </SidebarSection>
          )}
        </EventSidebar>
      </EventContent>

      {/* Ticket Purchase Modal removed: paid events use Get Access (purchase+register) */}

      {/* Ticket Code Redeem Modal */}
      {showRedeemModal && user && event.ticketPrice > 0 && (
        <TicketModalOverlay onClick={() => setShowRedeemModal(false)}>
          <TicketModalContent onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>{t('eventdetails.redeem_ticket_code', 'Redeem Ticket Code')}</h3>
            <p style={{ marginBottom: '1rem', color: '#718096' }}>
              {t('eventdetails.redeem_ticket_code_help', 'Enter the ticket code you received from the organizer to get access to this event.')}
            </p>
            <input
              type="text"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              placeholder={t('eventdetails.enter_ticket_code', 'Enter your ticket code')}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #CBD5E0',
                marginBottom: '0.75rem'
              }}
            />
            {redeemError && (
              <p style={{ color: '#E53E3E', marginBottom: '0.75rem' }}>{redeemError}</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <Button variant="secondary" size="small" onClick={() => setShowRedeemModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleRedeemCode}
                disabled={redeeming}
              >
                {redeeming ? t('eventdetails.validating', 'Validating...') : t('eventdetails.redeem_code', 'Redeem Code')}
              </Button>
            </div>
          </TicketModalContent>
        </TicketModalOverlay>
      )}
    </Container>
  );
};

// Styled Components
const BackButton = styled.div`
  margin-bottom: 2rem;
  
  a {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EventHeader = styled.div`
  position: relative;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin-bottom: 2rem;
`;

const EventCoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const EventHeaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
`;

const EventBadge = styled.div`
  background: ${props => props.color};
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
`;

const EventStatus = styled.div`
  background: ${props => props.color};
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
`;

const EventContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const EventMainInfo = styled.div``;

const EventTitle = styled.h1`
  color: ${({ theme }) => theme.color.darkerText};
  margin: 0 0 1rem 0;
  font-size: 2.5rem;
  font-weight: 700;
`;

const EventDescription = styled.p`
  color: ${({ theme }) => theme.color.lighterText};
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const EventMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const EventMetaItem = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const MetaLabel = styled.div`
  color: ${({ theme }) => theme.color.lighterText};
  font-size: 14px;
  margin-bottom: 0.5rem;
`;

const MetaValue = styled.div`
  color: ${({ theme }) => theme.color.darkerText};
  font-weight: 600;
  font-size: 16px;
`;

const RegistrationSection = styled.div`
  margin: 2rem 0;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const TournamentSection = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  margin-top: 2rem;
  
  h3 {
    color: ${({ theme }) => theme.color.darkerText};
    margin: 0 0 1rem 0;
  }
`;

const EventSidebar = styled.div``;

const SidebarSection = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  color: ${({ theme }) => theme.color.darkerText};
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
`;

const ParticipantsList = styled.div``;

const EmptyParticipants = styled.div`
  color: ${({ theme }) => theme.color.lighterText};
  text-align: center;
  padding: 2rem;
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ParticipantAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  div {
    color: white;
    font-weight: 600;
    font-size: 16px;
  }
`;

const ParticipantInfo = styled.div`
  flex: 1;
`;

const ParticipantName = styled.div`
  color: ${({ theme }) => theme.color.darkerText};
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ParticipantStatus = styled.div`
  color: ${props => props.status === 'approved' ? '#10B981' :
    props.status === 'pending' ? '#F59E0B' :
      props.theme.color.lighterText};
  font-size: 12px;
  text-transform: capitalize;
`;

const ShowMoreParticipants = styled.div`
  color: ${({ theme }) => theme.color.lighterText};
  text-align: center;
  padding: 1rem;
  font-size: 14px;
`;

const EventRules = styled.div`
  color: ${({ theme }) => theme.color.lighterText};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TicketModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const TicketModalContent = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 12px;
  padding: 24px;
  max-width: 560px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

export default EventDetails;
